/**
 * "La Reception": l'agente che risponde (e invia) da solo alle email dei clienti.
 * Qui il titolare accende l'autopilota, vede tutto quello che ha fatto, approva
 * le risposte in coda e istruisce la Reception (sito, FAQ, regole, caparra).
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  ConciergeBell, Loader2, Globe, Plus, Trash2, Send, X, ShieldAlert,
  Check, Moon, BadgeEuro, ChevronDown, ChevronUp, Sparkles,
} from 'lucide-react';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  accountsApi, receptionApi,
  type ReceptionLogItem, type ReceptionSettings,
} from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

const INTENTS: { key: string; label: string }[] = [
  { key: 'info', label: 'Informazioni (orari, servizi, indicazioni)' },
  { key: 'prenotazione', label: 'Prenotazioni' },
  { key: 'appuntamento', label: 'Appuntamenti' },
  { key: 'preventivo', label: 'Preventivi' },
];

const ACTION_META: Record<ReceptionLogItem['action'], { label: string; cls: string }> = {
  sent: { label: 'Inviata da sola', cls: 'bg-emerald-100 text-emerald-700' },
  approved: { label: 'Approvata e inviata', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Da approvare', cls: 'bg-amber-100 text-amber-700' },
  escalated: { label: 'Passata a te', cls: 'bg-blue-100 text-blue-700' },
  rejected: { label: 'Rifiutata', cls: 'bg-zinc-100 text-zinc-600' },
  skipped: { label: 'Ignorata', cls: 'bg-zinc-100 text-zinc-500' },
};

export function ReceptionPage() {
  const qc = useQueryClient();
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => accountsApi.list() });
  const accounts = accountsQuery.data?.accounts ?? [];
  const [selId, setSelId] = useState<string | null>(null);
  const accountId = selId ?? accounts.find((a) => a.is_default)?.id ?? accounts[0]?.id ?? null;

  const settingsQuery = useQuery({
    queryKey: ['reception-settings', accountId],
    queryFn: () => receptionApi.settings(accountId!),
    enabled: !!accountId,
  });
  const logQuery = useQuery({
    queryKey: ['reception-log', accountId],
    queryFn: () => receptionApi.log(accountId!),
    enabled: !!accountId,
    refetchInterval: 20_000,
  });

  const s = settingsQuery.data?.settings ?? null;
  const kbChunks = settingsQuery.data?.kbChunks ?? 0;
  const stats = logQuery.data?.stats;
  const items = logQuery.data?.items ?? [];
  const pending = items.filter((i) => i.action === 'pending');
  const feed = items.filter((i) => i.action !== 'pending' && i.action !== 'skipped');

  const saveMut = useMutation({
    mutationFn: (data: Parameters<typeof receptionApi.saveSettings>[0]) => receptionApi.saveSettings(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reception-settings'] }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Salvataggio fallito'),
  });

  function patch(data: Partial<Parameters<typeof receptionApi.saveSettings>[0]>) {
    if (!accountId) return;
    saveMut.mutate({ accountId, ...data });
  }

  if (!accountId) {
    return (
      <div className="h-screen flex bg-background">
        <InboxSidebar />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">Collega prima una casella.</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-8 space-y-6">

          {/* Hero: interruttore */}
          <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
                  <ConciergeBell className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-xl font-bold leading-tight">La Reception</h1>
                  <p className="text-sm text-white/85 mt-1 max-w-md">
                    Risponde ai clienti al posto tuo, giorno e notte, usando il tuo sito e le tue regole.
                    Tu vedi tutto e puoi fermarla quando vuoi.
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <Switch
                  checked={s?.enabled ?? false}
                  onCheckedChange={(v) => {
                    if (v && kbChunks === 0) { toast.error('Prima istruiscila: "Costruisci dal sito" o aggiungi una FAQ.'); return; }
                    patch({ enabled: v });
                  }}
                  className="scale-125 data-[state=checked]:bg-emerald-400"
                />
                <span className="text-xs font-semibold">{s?.enabled ? 'IN SERVIZIO' : 'SPENTA'}</span>
              </div>
            </div>

            {accounts.length > 1 && (
              <select
                value={accountId}
                onChange={(e) => setSelId(e.target.value)}
                className="mt-4 text-sm rounded-md bg-white/15 border border-white/25 px-2 py-1.5 text-white [&>option]:text-black"
              >
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.email_address}</option>)}
              </select>
            )}

            {/* Modalità */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              {([['approval', 'Chiede la tua approvazione', 'Prepara la risposta, tu la confermi con un tap'], ['auto', 'Risponde e invia da sola', 'Autonomia piena sulle richieste sicure']] as const).map(([mode, title, sub]) => (
                <button
                  key={mode}
                  onClick={() => patch({ mode })}
                  className={cn(
                    'text-left rounded-xl border p-3 transition-colors',
                    s?.mode === mode ? 'bg-white text-indigo-700 border-white' : 'border-white/30 text-white/90 hover:bg-white/10'
                  )}
                >
                  <div className="text-sm font-bold">{title}</div>
                  <div className={cn('text-xs mt-0.5', s?.mode === mode ? 'text-indigo-500' : 'text-white/70')}>{sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Contatori */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { v: stats?.today_sent ?? 0, l: 'Risposte oggi', icon: <Send className="h-4 w-4 text-emerald-600" /> },
              { v: stats?.month_sent ?? 0, l: 'Questo mese', icon: <Sparkles className="h-4 w-4 text-indigo-500" /> },
              { v: stats?.off_hours ?? 0, l: 'Fuori orario', icon: <Moon className="h-4 w-4 text-violet-500" /> },
              { v: stats?.pending ?? 0, l: 'Da approvare', icon: <ShieldAlert className="h-4 w-4 text-amber-500" /> },
            ].map((c) => (
              <div key={c.l} className="rounded-xl border p-3">
                <div className="flex items-center justify-between">{c.icon}<span className="text-2xl font-bold">{c.v}</span></div>
                <div className="text-xs text-muted-foreground mt-1">{c.l}</div>
              </div>
            ))}
          </div>

          {/* Coda approvazioni */}
          {pending.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-500" /> In attesa della tua approvazione ({pending.length})
                </CardTitle>
                <CardDescription>Rivedi, correggi se serve (la Reception impara dalle tue correzioni) e invia.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {pending.map((p) => <PendingItem key={p.id} item={p} onDone={() => { qc.invalidateQueries({ queryKey: ['reception-log'] }); }} />)}
              </CardContent>
            </Card>
          )}

          {/* key: rimonta i form (defaultValue) quando i dati arrivano o cambi casella */}
          <KnowledgeCard key={`kb-${accountId}-${settingsQuery.dataUpdatedAt}`} accountId={accountId} settings={s} kbChunks={kbChunks} onPatch={patch} />
          <RulesCard key={`rules-${accountId}-${settingsQuery.dataUpdatedAt}`} settings={s} onPatch={patch} saving={saveMut.isPending} />

          {/* Attività */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Attività recente</CardTitle>
              <CardDescription>Ogni azione della Reception, tracciata. Digest ogni sera alle 19.</CardDescription>
            </CardHeader>
            <CardContent>
              {feed.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Ancora nulla. Accendila e lascia che la posta arrivi: la vedrai lavorare qui.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {feed.map((i) => <FeedItem key={i.id} item={i} />)}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PendingItem({ item, onDone }: { item: ReceptionLogItem; onDone: () => void }) {
  const [body, setBody] = useState(item.reply_body ?? '');
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);

  async function act(kind: 'approve' | 'reject') {
    setBusy(kind);
    try {
      if (kind === 'approve') await receptionApi.approve(item.id, body.trim() !== (item.reply_body ?? '').trim() ? body : undefined);
      else await receptionApi.reject(item.id);
      toast.success(kind === 'approve' ? 'Inviata!' : 'Scartata');
      onDone();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Operazione fallita');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-lg border bg-amber-50/40 p-3 space-y-2">
      <div className="text-sm">
        <span className="font-semibold">{item.from_email}</span>
        <span className="text-muted-foreground"> · {item.orig_subject || '(senza oggetto)'} · {item.intent}</span>
      </div>
      {item.reason && <div className="text-xs text-muted-foreground">Perché in coda: {item.reason}</div>}
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="bg-background text-sm" />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void act('approve')} disabled={busy !== null || !body.trim()}>
          {busy === 'approve' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
          Approva e invia
        </Button>
        <Button size="sm" variant="outline" onClick={() => void act('reject')} disabled={busy !== null}>
          <X className="h-3.5 w-3.5 mr-1.5" /> Scarta
        </Button>
      </div>
    </div>
  );
}

function FeedItem({ item }: { item: ReceptionLogItem }) {
  const [open, setOpen] = useState(false);
  const meta = ACTION_META[item.action];
  return (
    <div className="rounded-md border px-3 py-2">
      <button className="w-full flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)}>
        <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0', meta.cls)}>{meta.label}</span>
        <span className="text-sm truncate flex-1">
          <span className="font-medium">{item.from_email}</span>
          <span className="text-muted-foreground"> · {item.orig_subject || '(senza oggetto)'}</span>
        </span>
        <span className="text-xs text-muted-foreground shrink-0">
          {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: it })}
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t text-sm space-y-1.5">
          {item.reason && <div className="text-xs text-muted-foreground">Motivo: {item.reason}</div>}
          {item.reply_body && <pre className="whitespace-pre-wrap font-sans text-sm bg-muted/50 rounded-md p-2">{item.reply_body}</pre>}
        </div>
      )}
    </div>
  );
}

function KnowledgeCard({ accountId, settings, kbChunks, onPatch }: {
  accountId: string;
  settings: ReceptionSettings | null;
  kbChunks: number;
  onPatch: (d: Record<string, unknown>) => void;
}) {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [crawling, setCrawling] = useState(false);
  const [faqTitle, setFaqTitle] = useState('');
  const [faqBody, setFaqBody] = useState('');
  const kbQuery = useQuery({ queryKey: ['reception-kb', accountId], queryFn: () => receptionApi.kb(accountId) });
  const effectiveUrl = url || settings?.business_url || '';

  async function crawl() {
    if (!effectiveUrl.trim()) { toast.error('Inserisci l’indirizzo del tuo sito'); return; }
    setCrawling(true);
    try {
      const r = await receptionApi.crawl(accountId, effectiveUrl.trim());
      toast.success(`Fatto: ${r.pages} pagine lette, ${r.chunks} nozioni imparate`);
      qc.invalidateQueries({ queryKey: ['reception-kb'] });
      qc.invalidateQueries({ queryKey: ['reception-settings'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Lettura del sito fallita');
    } finally {
      setCrawling(false);
    }
  }

  async function addFaq() {
    if (!faqTitle.trim() || !faqBody.trim()) return;
    try {
      await receptionApi.addKb(accountId, faqTitle.trim(), faqBody.trim());
      setFaqTitle(''); setFaqBody('');
      toast.success('FAQ aggiunta');
      qc.invalidateQueries({ queryKey: ['reception-kb'] });
      qc.invalidateQueries({ queryKey: ['reception-settings'] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Aggiunta fallita');
    }
  }

  const manual = (kbQuery.data?.items ?? []).filter((i) => i.source === 'manual');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Globe className="h-4 w-4 text-indigo-500" /> Cosa sa la Reception</CardTitle>
        <CardDescription>
          {kbChunks > 0
            ? `${kbChunks} nozioni in memoria${settings?.last_crawl_at ? ` · sito letto ${formatDistanceToNow(new Date(settings.last_crawl_at), { addSuffix: true, locale: it })}` : ''}. Risponde SOLO con queste informazioni.`
            : 'La Reception risponde solo con ciò che le insegni: parti dal tuo sito.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label>Nome attività</Label>
          <Input
            defaultValue={settings?.business_name ?? ''}
            placeholder="Es. Bistrot Aurora"
            onBlur={(e) => { if (e.target.value.trim() !== (settings?.business_name ?? '')) onPatch({ businessName: e.target.value.trim() || null }); }}
          />
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1.5">
            <Label>Il tuo sito</Label>
            <Input value={url || settings?.business_url || ''} onChange={(e) => setUrl(e.target.value)} placeholder="www.iltuosito.it" />
          </div>
          <Button onClick={() => void crawl()} disabled={crawling}>
            {crawling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Globe className="h-4 w-4 mr-2" />}
            {kbChunks > 0 ? 'Rileggi il sito' : 'Costruisci dal sito'}
          </Button>
        </div>

        <div className="rounded-lg border p-3 space-y-2">
          <div className="text-sm font-medium">Aggiungi una FAQ a mano</div>
          <Input value={faqTitle} onChange={(e) => setFaqTitle(e.target.value)} placeholder="Es. Politica cancellazioni" />
          <Textarea value={faqBody} onChange={(e) => setFaqBody(e.target.value)} rows={2} placeholder="Es. Le prenotazioni si possono annullare gratuitamente fino a 24 ore prima…" />
          <Button size="sm" variant="outline" onClick={() => void addFaq()} disabled={!faqTitle.trim() || !faqBody.trim()}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Aggiungi
          </Button>
          {manual.length > 0 && (
            <div className="pt-1 space-y-1">
              {manual.map((f) => (
                <div key={f.id} className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{f.title}</span>
                  <span className="text-muted-foreground text-xs truncate flex-1">{f.preview}</span>
                  <button
                    onClick={() => { void receptionApi.removeKb(f.id).then(() => qc.invalidateQueries({ queryKey: ['reception-kb'] })); }}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function RulesCard({ settings, onPatch, saving }: {
  settings: ReceptionSettings | null;
  onPatch: (d: Record<string, unknown>) => void;
  saving: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-indigo-500" /> Regole e limiti</CardTitle>
        <CardDescription>Cosa può gestire da sola e quando deve passarti la palla. {saving ? 'Salvo…' : ''}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {INTENTS.map((i) => (
            <div key={i.key} className="flex items-center justify-between rounded-md border px-3 py-2">
              <span className="text-sm">{i.label}</span>
              <Switch
                checked={settings?.allowed_intents?.[i.key] !== false}
                onCheckedChange={(v) => onPatch({ allowedIntents: { ...(settings?.allowed_intents ?? {}), [i.key]: v } })}
              />
            </div>
          ))}
          <p className="text-xs text-muted-foreground">Reclami, questioni legali e fornitori passano SEMPRE a te.</p>
        </div>

        <div className="space-y-1.5">
          <Label>Quando deve passarti la palla (parole tue)</Label>
          <Textarea
            defaultValue={settings?.escalation_rules ?? ''}
            rows={2}
            placeholder="Es. gruppi sopra le 10 persone, eventi privati, richieste sopra i 500 euro, giornalisti"
            onBlur={(e) => { if (e.target.value !== (settings?.escalation_rules ?? '')) onPatch({ escalationRules: e.target.value || null }); }}
          />
        </div>

        <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-2">
          <div className="text-sm font-semibold flex items-center gap-1.5"><BadgeEuro className="h-4 w-4 text-indigo-600" /> Caparra e pagamenti</div>
          <p className="text-xs text-muted-foreground">
            Incolla un link di pagamento (Stripe, PayPal, Satispay…): quando la tua policy lo prevede, la Reception
            chiude la conversazione con il bottone “Conferma e paga la caparra”.
          </p>
          <Input
            defaultValue={settings?.payment_link ?? ''}
            placeholder="https://buy.stripe.com/…"
            onBlur={(e) => { if (e.target.value.trim() !== (settings?.payment_link ?? '')) onPatch({ paymentLink: e.target.value.trim() || null }); }}
          />
          <Input
            defaultValue={settings?.payment_policy ?? ''}
            placeholder="Quando chiederla? Es. sempre per gruppi sopra 6 persone e per gli eventi"
            onBlur={(e) => { if (e.target.value.trim() !== (settings?.payment_policy ?? '')) onPatch({ paymentPolicy: e.target.value.trim() || null }); }}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Prudenza (confidenza minima)</Label>
            <select
              value={String(Number(settings?.min_confidence ?? 0.75))}
              onChange={(e) => onPatch({ minConfidence: parseFloat(e.target.value) })}
              className="w-full h-9 rounded-md border bg-background px-2 text-sm"
            >
              <option value="0.9">Molto prudente (0.90)</option>
              <option value="0.75">Equilibrata (0.75)</option>
              <option value="0.6">Intraprendente (0.60)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Massimo risposte al giorno</Label>
            <Input
              type="number"
              defaultValue={settings?.max_replies_per_day ?? 40}
              min={1} max={200}
              onBlur={(e) => { const n = parseInt(e.target.value, 10); if (n && n !== settings?.max_replies_per_day) onPatch({ maxRepliesPerDay: n }); }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
